import { RefObject, useCallback, useMemo } from 'react';
import ManageGrid from './table/ManageGrid';
import { mapItemsToSearchItems } from '../../../../components/Table/types/index';

interface TabGridContentProps {
  panelTab: string;
  selectedView: 'tables' | 'graphs';
  showSummary: boolean;
  showEvidence: boolean;
  activeColumnPanel: 'summary' | 'evidence';
  // Shared ManageGrid props
  source: any;
  view: any;
  tab: any;
  searchItems: any;
  setSearchItems: any;
  restrictChemical: any;
  isInfoSidebarOpen: boolean;
  setIsInfoSidebarOpen: (open: boolean) => void;
  defaultFilterValue: any;
  dispatch: any;
  sourceWeights: Array<{ name: string; score: number }>;
  onScoreSaved: () => void;
  onOpenSearchItemInfo: (item: any) => void;
  summaryEvidenceToggle: any;
  // Summary column state
  summaryColumnModel: Record<string, boolean> | undefined;
  setSummaryColumnModel: (updater: any) => void;
  summaryColumnOrder: string[] | undefined;
  setSummaryColumnOrder: (order: string[]) => void;
  summaryResetRef: RefObject<(() => void) | null>;
  // Evidence column state
  evidenceColumnModel: Record<string, boolean> | undefined;
  setEvidenceColumnModel: (updater: any) => void;
  evidenceColumnOrder: string[] | undefined;
  setEvidenceColumnOrder: (order: string[]) => void;
  evidenceResetRef: RefObject<(() => void) | null>;
}

export function TabGridContent({
  panelTab,
  selectedView,
  showSummary,
  showEvidence,
  activeColumnPanel,
  source,
  view,
  tab,
  searchItems,
  setSearchItems,
  restrictChemical,
  isInfoSidebarOpen,
  setIsInfoSidebarOpen,
  defaultFilterValue,
  dispatch,
  sourceWeights,
  onScoreSaved,
  onOpenSearchItemInfo,
  summaryEvidenceToggle,
  summaryColumnModel,
  setSummaryColumnModel,
  summaryColumnOrder,
  setSummaryColumnOrder,
  summaryResetRef,
  evidenceColumnModel,
  setEvidenceColumnModel,
  evidenceColumnOrder,
  setEvidenceColumnOrder,
  evidenceResetRef,
}: TabGridContentProps) {
  const isEvidence = showEvidence && (!showSummary || (showSummary && activeColumnPanel === 'evidence'));

  const setColumnVisibilityModel = useCallback(
    (updaterOrValue: any) => {
      if (isEvidence) {
        setEvidenceColumnModel((prev: any) =>
          typeof updaterOrValue === 'function' ? updaterOrValue(prev ?? {}) : updaterOrValue,
        );
      } else {
        setSummaryColumnModel((prev: any) =>
          typeof updaterOrValue === 'function' ? updaterOrValue(prev ?? {}) : updaterOrValue,
        );
      }
    },
    [isEvidence, setEvidenceColumnModel, setSummaryColumnModel],
  );

  const setColumnOrder = useCallback(
    (order: string[]) => (isEvidence ? setEvidenceColumnOrder(order) : setSummaryColumnOrder(order)),
    [isEvidence, setEvidenceColumnOrder, setSummaryColumnOrder],
  );

  const onColumnStateInit = useCallback(
    (isEv: boolean, model: any, order: string[] | null) => {
      if (isEv) {
        setEvidenceColumnModel(model);
        setEvidenceColumnOrder(order ?? undefined);
      } else {
        setSummaryColumnModel(model);
        setSummaryColumnOrder(order ?? undefined);
      }
    },
    [setEvidenceColumnModel, setEvidenceColumnOrder, setSummaryColumnModel, setSummaryColumnOrder],
  );

  const mappedSearchItems = useMemo(() => mapItemsToSearchItems(searchItems), [searchItems]);

  if (selectedView !== 'tables') return null;
  if (!showSummary && !showEvidence) return null;

  const columnVisibilityModel = isEvidence ? evidenceColumnModel : summaryColumnModel;
  const columnOrder = isEvidence ? evidenceColumnOrder : summaryColumnOrder;
  const resetRef = isEvidence ? evidenceResetRef : summaryResetRef;

  return (
    <ManageGrid
      source={source}
      view={view}
      tab={tab}
      panelTab={panelTab}
      searchItems={mappedSearchItems}
      setSearchItems={setSearchItems}
      isEvidence={isEvidence}
      isMapping={false}
      restrictChemical={restrictChemical}
      isInfoSidebarOpen={isInfoSidebarOpen}
      setIsInfoSidebarOpen={setIsInfoSidebarOpen}
      defaultFilterValue={defaultFilterValue}
      dispatch={dispatch}
      sourceWeights={sourceWeights}
      onScoreSaved={onScoreSaved}
      onOpenSearchItemInfo={onOpenSearchItemInfo}
      columnVisibilityModel={columnVisibilityModel}
      setColumnVisibilityModel={setColumnVisibilityModel}
      columnOrder={columnOrder}
      setColumnOrder={setColumnOrder}
      onColumnStateInit={onColumnStateInit}
      showRightSidebar={false}
      resetRef={resetRef}
      summaryEvidenceToggle={summaryEvidenceToggle}
    />
  );
}
